import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Divider,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  NotificationsNone as NotificationsNoneIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import { mt5API, accountsAPI, customersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import TelegramSettings from '../components/TelegramSettings';

// Source options cho dropdown — giữ đồng nhất với TelegramSettings.
const SOURCE_OPTS = [
  { v: 'all',        label: 'Tất cả' },
  { v: 'psychology', label: 'Đánh giá cảm xúc (TPM)' },
  { v: 'sync',       label: 'Sync Checker' },
  { v: 'slave',      label: 'Slave EA' },
  { v: 'http',       label: 'HTTP / khác' },
  { v: 'websocket',  label: 'WebSocket / khác' },
];

const ALERT_COLORS = {
  error: 'error',
  warning: 'warning',
  info: 'info',
  success: 'success',
};

const ALERT_LABELS = {
  error: 'Lỗi',
  warning: 'Cảnh báo',
  info: 'Thông tin',
  success: 'Thành công',
};

const ITEMS_PER_PAGE = 50;

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('vi-VN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export default function Notifications() {
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterAccount, setFilterAccount] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [search, setSearch] = useState('');
  const [deletingIds, setDeletingIds] = useState(new Set());
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  // Filter object pass cho API — chỉ gửi field non-default (server hiểu cả 2).
  const filters = {
    ...(search ? { q: search } : {}),
    ...(filterType !== 'all' ? { type: filterType } : {}),
    ...(filterSource !== 'all' ? { source: filterSource } : {}),
    ...(filterAccount ? { account_id: filterAccount } : {}),
    ...(filterCustomer ? { customer_id: filterCustomer } : {}),
  };

  // queryKey gồm cả filters → đổi filter tự refetch + cache đúng từng combo.
  const { data, isLoading, isError } = useQuery(
    ['alertHistory', page, filters],
    () => mt5API.getAlertHistory(page, ITEMS_PER_PAGE, filters).then(r => r.data),
    { keepPreviousData: true, refetchInterval: 30000 },
  );

  // Dropdown options — fetch 1 lần, cache lâu
  const { data: accountsData } = useQuery(
    'accounts-options', () => accountsAPI.getAll(),
    { staleTime: 60000 },
  );
  const { data: customersData } = useQuery(
    'customers-options', () => customersAPI.getOptions(),
    { staleTime: 60000 },
  );
  const accountsList  = accountsData?.data || [];
  const customersList = customersData?.data || [];

  const alerts = data?.alerts || [];
  const total = data?.total || 0;
  const pageCount = Math.ceil(total / ITEMS_PER_PAGE);

  const handleDelete = useCallback(async (id) => {
    setDeletingIds(prev => new Set(prev).add(id));
    try {
      await mt5API.deleteAlert(id);
      queryClient.invalidateQueries('alertHistory');
    } finally {
      setDeletingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  }, [queryClient]);

  // Server đã filter rồi — UI không cần lọc lại nữa.
  const filtered = alerts;

  // Pagination handler: KHÔNG reset filter/search (đó là bug cũ).
  const handlePageChange = useCallback((_, value) => {
    setPage(value);
  }, []);

  // Helper: khi đổi filter → reset về page 1 (vì total đã đổi).
  const resetToFirstPage = () => setPage(1);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          Thông báo
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Lịch sử tất cả cảnh báo từ hệ thống MT5
        </Typography>
      </Box>

      {/* Telegram channel manager (admin only) */}
      {isAdmin() && <TelegramSettings />}

      {/* Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
          backgroundColor: 'background.paper',
        }}
      >
        <TextField
          size="small"
          placeholder="Tìm theo tài khoản, tên, nội dung..."
          value={search}
          onChange={e => { setSearch(e.target.value); resetToFirstPage(); }}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Loại</InputLabel>
          <Select
            value={filterType}
            label="Loại"
            onChange={e => { setFilterType(e.target.value); resetToFirstPage(); }}
          >
            <MenuItem value="all">Tất cả</MenuItem>
            <MenuItem value="error">Lỗi</MenuItem>
            <MenuItem value="warning">Cảnh báo</MenuItem>
            <MenuItem value="info">Thông tin</MenuItem>
            <MenuItem value="success">Thành công</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Nguồn</InputLabel>
          <Select
            value={filterSource}
            label="Nguồn"
            onChange={e => { setFilterSource(e.target.value); resetToFirstPage(); }}
          >
            {SOURCE_OPTS.map(o => (
              <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Tài khoản</InputLabel>
          <Select
            value={filterAccount}
            label="Tài khoản"
            onChange={e => { setFilterAccount(e.target.value); resetToFirstPage(); }}
          >
            <MenuItem value="">Tất cả ({accountsList.length})</MenuItem>
            {accountsList.map(a => (
              <MenuItem key={a.mt5_account_id} value={a.mt5_account_id}>
                {a.name ? `${a.name} (${a.mt5_account_id})` : a.mt5_account_id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Khách hàng</InputLabel>
          <Select
            value={filterCustomer}
            label="Khách hàng"
            onChange={e => { setFilterCustomer(e.target.value); resetToFirstPage(); }}
          >
            <MenuItem value="">Tất cả ({customersList.length})</MenuItem>
            {customersList.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ color: 'text.secondary', ml: 'auto' }}>
          Tổng: <strong>{total}</strong> thông báo
        </Typography>
      </Paper>

      {/* Content */}
      <Paper sx={{ backgroundColor: 'background.paper', overflow: 'hidden' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {isError && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">Không thể tải lịch sử thông báo.</Alert>
          </Box>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, color: 'text.secondary' }}>
            <NotificationsNoneIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.4 }} />
            <Typography variant="body1">Không có thông báo nào</Typography>
          </Box>
        )}

        {!isLoading && !isError && filtered.length > 0 && (
          <Stack divider={<Divider />}>
            {filtered.map((alert, i) => (
              <Box
                key={alert.id ?? i}
                sx={{
                  px: 3,
                  py: 1.8,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Chip
                    label={ALERT_LABELS[alert.alert_type] || alert.alert_type || '—'}
                    color={ALERT_COLORS[alert.alert_type] || 'default'}
                    size="small"
                    sx={{ mt: 0.3, flexShrink: 0, fontSize: '0.72rem', fontWeight: 600 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.primary', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {alert.message || '(Không có nội dung)'}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                      {(alert.slave_name || alert.slave_account_id) && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Slave: {alert.slave_name || alert.slave_account_id}
                        </Typography>
                      )}
                      {(alert.master_name || alert.master_account_id) && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Master: {alert.master_name || alert.master_account_id}
                        </Typography>
                      )}
                      {alert.sync_status && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Sync: {alert.sync_status}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', flexShrink: 0, pt: 0.2, fontSize: '0.7rem' }}
                  >
                    {formatDate(alert.received_at)}
                  </Typography>
                  <Tooltip title="Xóa thông báo này">
                    <IconButton
                      size="small"
                      disabled={deletingIds.has(alert.id)}
                      onClick={() => handleDelete(alert.id)}
                      sx={{ flexShrink: 0, opacity: 0.4, '&:hover': { opacity: 1, color: 'error.main' }, transition: 'opacity 0.15s' }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Stack>
        )}

        {/* Pagination */}
        {!isLoading && pageCount > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
}
